import { BREAKPOINTS, LayoutBreakpoint } from '@/styles/layout';
import { remCalc } from '@/styles/typography/utils';

export const square = (size: number) => ({
  width: remCalc(size),
  height: remCalc(size),
});

/**
 * Creates horizontal padding styles (left and right) with rem units.
 * If only one value is provided, it applies to both sides.
 *
 * @param paddingLeft - The left padding value in pixels (will be converted to rem)
 * @param paddingRight - Optional right padding value. If omitted, uses paddingLeft value
 * @returns An object with paddingLeft and paddingRight CSS properties in rem units
 * @example
 * paddingX(16) // { paddingLeft: '1rem', paddingRight: '1rem' }
 * paddingX(16, 24) // { paddingLeft: '1rem', paddingRight: '1.5rem' }
 */
export const paddingX = (paddingLeft: number, paddingRight?: number) => ({
  paddingLeft: remCalc(paddingLeft),
  paddingRight: remCalc(paddingRight ?? paddingLeft),
});

/**
 * Creates vertical padding styles (top and bottom) with rem units.
 * If only one value is provided, it applies to both sides.
 *
 * @param paddingTop - The top padding value in pixels (will be converted to rem)
 * @param paddingBottom - Optional bottom padding value. If omitted, uses paddingTop value
 * @returns An object with paddingTop and paddingBottom CSS properties in rem units
 * @example
 * paddingY(16) // { paddingTop: '1rem', paddingBottom: '1rem' }
 * paddingY(16, 24) // { paddingTop: '1rem', paddingBottom: '1.5rem' }
 */
export const paddingY = (paddingTop: number, paddingBottom?: number) => ({
  paddingTop: remCalc(paddingTop),
  paddingBottom: remCalc(paddingBottom ?? paddingTop),
});

/**
 * Creates horizontal margin styles (left and right) with rem units.
 * Supports 'auto' values for centering elements. If only one value is provided, it applies to both sides.
 *
 * @param marginLeft - The left margin value in pixels (will be converted to rem) or 'auto'
 * @param marginRight - Optional right margin value. If omitted, uses marginLeft value. Can be a number or 'auto'
 * @returns An object with marginLeft and marginRight CSS properties in rem units or 'auto'
 * @example
 * marginX(16) // { marginLeft: '1rem', marginRight: '1rem' }
 * marginX('auto') // { marginLeft: 'auto', marginRight: 'auto' }
 * marginX(16, 24) // { marginLeft: '1rem', marginRight: '1.5rem' }
 */
export const marginX = (marginLeft: number | 'auto', marginRight?: number | 'auto') => {
  return {
    marginLeft: marginLeft === 'auto' ? marginLeft : remCalc(marginLeft),
    marginRight: marginRight === undefined ? marginLeft : marginRight === 'auto' ? marginRight : remCalc(marginRight),
  };
};

/**
 * Creates vertical margin styles (top and bottom) with rem units.
 * If only one value is provided, it applies to both sides.
 *
 * @param marginTop - The top margin value in pixels (will be converted to rem)
 * @param marginBottom - Optional bottom margin value. If omitted, uses marginTop value
 * @returns An object with marginTop and marginBottom CSS properties in rem units
 * @example
 * marginY(16) // { marginTop: '1rem', marginBottom: '1rem' }
 * marginY(16, 24) // { marginTop: '1rem', marginBottom: '1.5rem' }
 */
export const marginY = (marginTop: number, marginBottom?: number) => ({
  marginTop: remCalc(marginTop),
  marginBottom: remCalc(marginBottom ?? marginTop),
});

export const BREAKPOINT_VAR = 'var(--breakpoint-x)';
export const BREAKPOINT_PADDING_VAR = 'var(--breakpoint-padding-x)';

export const applyBreakpointPadding = {
  paddingLeft: BREAKPOINT_PADDING_VAR,
  paddingRight: BREAKPOINT_PADDING_VAR,
};

export const applyBreakpointNegativeMargins = {
  marginLeft: `calc(${BREAKPOINT_PADDING_VAR} * -1)`,
  marginRight: `calc(${BREAKPOINT_PADDING_VAR} * -1)`,
};

/**
 * Generates a CSS media query for screens at or above the specified breakpoint.
 * Uses min-width to target larger screens.
 *
 * @param layoutBreakpointName - The breakpoint name ('mobile', 'tablet', 'desktop', or 'desktopWide')
 * @returns A CSS media query string targeting screens at or above the breakpoint
 * @example
 * mediaBreakpointUp('tablet') // '@media (min-width: 40rem)'
 * // Applies styles on tablet and larger screens
 */
export const mediaBreakpointUp = (layoutBreakpointName: LayoutBreakpoint) =>
  `@media (min-width: ${remCalc(BREAKPOINTS[layoutBreakpointName])})`;

/**
 * Generates a CSS media query for screens below the specified breakpoint.
 * Uses max-width to target smaller screens. The max-width is set to one pixel
 * less than the next breakpoint (if one exists).
 *
 * @param layoutBreakpointName - The breakpoint name ('mobile', 'tablet', 'desktop', or 'desktopWide')
 * @returns A CSS media query string targeting screens below the breakpoint
 * @example
 * mediaBreakpointDown('tablet') // '@media (max-width: 64rem)'
 * // Applies styles on screens smaller than tablet (mobile only)
 */
export const mediaBreakpointDown = (layoutBreakpointName: LayoutBreakpoint) => {
  let pixels = 0;

  const index = Object.keys(BREAKPOINTS).findIndex((breakpoint) => breakpoint === layoutBreakpointName);
  const nextBreakpoint = Object.values(BREAKPOINTS)[index + 1];

  if (nextBreakpoint) {
    pixels = nextBreakpoint - 1;
  }

  return `@media (max-width: ${remCalc(pixels)})`;
};

/**
 * Generates a CSS media query for screens between two specified breakpoints (inclusive).
 * Automatically sorts the breakpoints to ensure correct min/max ordering.
 *
 * @param layoutBreakpointName1 - The first breakpoint name ('mobile', 'tablet', 'desktop', or 'desktopWide')
 * @param layoutBreakpointName2 - The second breakpoint name ('mobile', 'tablet', 'desktop', or 'desktopWide')
 * @returns A CSS media query string targeting screens between the two breakpoints
 * @example
 * mediaBreakpointBetween('tablet', 'desktop') // '@media (min-width: 40rem) and (max-width: 64rem)'
 * // Applies styles on tablet and desktop screens only
 */
export const mediaBreakpointBetween = (
  layoutBreakpointName1: LayoutBreakpoint,
  layoutBreakpointName2: LayoutBreakpoint,
) => {
  const sorted = [BREAKPOINTS[layoutBreakpointName1], BREAKPOINTS[layoutBreakpointName2]].sort((a, b) => a - b);

  return `@media (min-width: ${remCalc(sorted[0])}) and (max-width: ${remCalc(sorted[1])})`;
};

/**
 * Generates a CSS media query for screens at exactly the specified breakpoint.
 * Targets only the range for that specific breakpoint (from its min-width to just before the next breakpoint).
 * For the first breakpoint (mobile), it targets up to the next breakpoint.
 * For the last breakpoint, it targets from that breakpoint and above.
 *
 * @param layoutBreakpointName - The breakpoint name ('mobile', 'tablet', 'desktop', or 'desktopWide')
 * @returns A CSS media query string targeting screens at the exact breakpoint range
 * @example
 * mediaBreakpointOnly('tablet') // '@media (min-width: 40rem) and (max-width: 64rem)'
 * // Applies styles only on tablet screens (not mobile, desktop, or desktopWide)
 */
export const mediaBreakpointOnly = (layoutBreakpointName: LayoutBreakpoint) => {
  let min = 0;
  let max = 0;

  const index = Object.keys(BREAKPOINTS).findIndex((breakpoint) => breakpoint === layoutBreakpointName);

  if (index === 0) {
    max = Object.values(BREAKPOINTS)[1] - 1;
  } else {
    min = Object.values(BREAKPOINTS)[index];

    const nextBreakpoint = Object.values(BREAKPOINTS)[index + 1];

    if (nextBreakpoint) {
      max = nextBreakpoint - 1;
    }
  }

  if (max > 0) {
    return `@media (min-width: ${remCalc(min)}) and (max-width: ${remCalc(max)})`;
  }

  return `@media (min-width: ${remCalc(BREAKPOINTS[layoutBreakpointName])})`;
};
